/**
 * MD3 Solar Hijri date field, picker, month picker, and range picker (FND-19: D1–D3).
 */

import {
  formatLocalDate,
  SOLAR_HIJRI_MONTHS_FA,
  type LocalDate,
  todayBusinessDate,
  toPersianDigits,
} from '@saay/formatting';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SegmentedButtons } from './actions.js';
import {
  buildSolarMonthGrid,
  type CalendarPanelView,
  clampRangeEndToMaxSpan,
  compareLocalDate,
  dateRangePresets,
  isBetweenInclusive,
  isDayDisabled,
  localDateFromSolarDigits,
  moveFocusedDay,
  rangeDayCount,
  selectRangeClick,
  shiftSolarMonth,
  solarDigitsFromLocalDate,
  solarMonthBounds,
  SOLAR_WEEKDAY_HEADERS_FA,
  visibleMonthForValue,
} from './headless/solar-calendar.js';
import { FieldSupportSlot, revealTruncatedLabel } from './field-support.js';
import { SOLAR_DATE_MASK } from './headless/mask-input.js';
import { Icon } from './icons.js';
import { BottomSheet } from './overlays.js';
import { IconButton } from './primitives.js';
import { TextField } from './text-fields.js';
import { useWindowClass } from './window-class.js';

export interface SolarDateFieldProps {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly label: ReactNode;
  readonly max?: LocalDate;
  readonly min?: LocalDate;
  readonly name?: string;
  readonly onChange: (value: LocalDate | '') => void;
  readonly today?: LocalDate;
  readonly value: LocalDate | '';
}

function validateBounds(
  value: LocalDate,
  min: LocalDate | undefined,
  max: LocalDate | undefined,
): string | undefined {
  if (min !== undefined && compareLocalDate(value, min) < 0) {
    return 'تاریخ معتبر نیست.';
  }
  if (max !== undefined && compareLocalDate(value, max) > 0) {
    return 'تاریخ معتبر نیست.';
  }
  return undefined;
}

/** D1: masked Solar Hijri field with a calendar affordance (material-standard §3.5). */
export function SolarDateField({
  disabled,
  error,
  hint,
  id,
  label,
  max,
  min,
  name,
  onChange,
  today = todayBusinessDate(),
  value,
}: SolarDateFieldProps) {
  const fieldId = useId();
  const [raw, setRaw] = useState(() => (value === '' ? '' : solarDigitsFromLocalDate(value)));
  const [open, setOpen] = useState(false);
  const [boundsError, setBoundsError] = useState<string | undefined>(undefined);
  const windowClass = useWindowClass('expanded');
  const fieldError = boundsError ?? error;

  useEffect(() => {
    if (value !== '' && localDateFromSolarDigits(raw) !== value) {
      setRaw(solarDigitsFromLocalDate(value));
    }
    if (value === '' && localDateFromSolarDigits(raw) !== null) {
      setRaw('');
    }
  }, [value]);

  const echo = value === '' ? undefined : formatLocalDate(value, 'long');
  const visible = visibleMonthForValue(value, min, today);

  function commitRaw(nextRaw: string) {
    setRaw(nextRaw);
    if (nextRaw.length === 0) {
      setBoundsError(undefined);
      onChange('');
      return;
    }
    const parsed = localDateFromSolarDigits(nextRaw);
    if (parsed === null) {
      return;
    }
    const bounds = validateBounds(parsed, min, max);
    setBoundsError(bounds);
    if (bounds === undefined) {
      onChange(parsed);
    }
  }

  const calendar = (
    <SolarCalendarPanel
      mode="single"
      onSelect={(next) => {
        onChange(next);
        setRaw(solarDigitsFromLocalDate(next));
        setBoundsError(undefined);
        setOpen(false);
      }}
      today={today}
      value={value}
      visibleMonth={visible}
      {...(max === undefined ? {} : { max })}
      {...(min === undefined ? {} : { min })}
    />
  );

  const calendarTrigger = (
    <IconButton
      aria-expanded={open}
      aria-haspopup="dialog"
      disabled={disabled}
      icon="calendar-today"
      label="انتخاب از تقویم"
      onClick={() => {
        if (windowClass === 'compact') {
          setOpen(true);
        }
      }}
      type="button"
      variant="standard"
    />
  );

  const field = (
    <TextField
      autoComplete="off"
      hint={
        echo === undefined ? (
          hint
        ) : (
          <>
            {echo}
            {hint === undefined ? null : <> — {hint}</>}
          </>
        )
      }
      id={id ?? fieldId}
      inputMode="numeric"
      label={label}
      ltr
      mask={SOLAR_DATE_MASK}
      maskRaw={raw}
      {...(disabled === undefined ? {} : { disabled })}
      {...(fieldError === undefined ? {} : { error: fieldError })}
      {...(name === undefined ? {} : { name })}
      onBlur={() => {
        if (raw.length === 8) {
          const parsed = localDateFromSolarDigits(raw);
          if (parsed === null) {
            setBoundsError('تاریخ معتبر نیست.');
            onChange('');
            return;
          }
          const bounds = validateBounds(parsed, min, max);
          setBoundsError(bounds);
          if (bounds === undefined) {
            onChange(parsed);
          }
        }
      }}
      onMaskRawChange={commitRaw}
      placeholder="سال/ماه/روز"
      suffix={
        windowClass === 'compact' ? (
          calendarTrigger
        ) : (
          <PopoverPrimitive.Trigger asChild>{calendarTrigger}</PopoverPrimitive.Trigger>
        )
      }
    />
  );

  return (
    <>
      {windowClass === 'compact' ? (
        <>
          {field}
          <BottomSheet
            footer={null}
            onOpenChange={setOpen}
            open={open}
            title={typeof label === 'string' ? label : 'تاریخ'}
          >
            {calendar}
          </BottomSheet>
        </>
      ) : (
        <PopoverPrimitive.Root onOpenChange={setOpen} open={open}>
          {field}
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="end"
              className="saay-popover__content saay-date-popover"
              dir="rtl"
              onOpenAutoFocus={(event) => event.preventDefault()}
              sideOffset={6}
            >
              {calendar}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
    </>
  );
}

export interface DatePickerProps {
  readonly disabled?: boolean;
  readonly label?: string;
  readonly max?: LocalDate;
  readonly min?: LocalDate;
  readonly onChange: (value: LocalDate) => void;
  readonly today?: LocalDate;
  readonly value: LocalDate;
}

/** D2: Solar Hijri day grid (popover on expanded, sheet on compact when embedded). */
export function DatePicker({
  disabled,
  label = 'تاریخ',
  max,
  min,
  onChange,
  today = todayBusinessDate(),
  value,
}: DatePickerProps) {
  const visible = visibleMonthForValue(value, min, today);
  return (
    <SolarCalendarPanel
      label={label}
      mode="single"
      onSelect={onChange}
      today={today}
      value={value}
      visibleMonth={visible}
      {...(disabled === undefined ? {} : { disabled })}
      {...(max === undefined ? {} : { max })}
      {...(min === undefined ? {} : { min })}
    />
  );
}

export interface MonthPickerProps {
  readonly disabled?: boolean;
  readonly max?: LocalDate;
  readonly min?: LocalDate;
  readonly month: number;
  readonly onMonthChange: (month: number) => void;
  /** Opens the year grid from the year title (owner: pick the year like the month). */
  readonly onYearTitleClick?: () => void;
  readonly onYearChange: (year: number) => void;
  readonly year: number;
}

/** Solar Hijri month grid for one year (D2 year step). */
export function MonthPicker({
  disabled,
  max,
  min,
  month,
  onMonthChange,
  onYearTitleClick,
  onYearChange,
  year,
}: MonthPickerProps) {
  const labelId = useId();
  return (
    <div aria-labelledby={labelId} className="saay-month-picker" role="group">
      <div className="saay-calendar__header">
        <IconButton
          disabled={disabled}
          icon="chevron-right"
          label="سال قبل"
          onClick={() => onYearChange(year - 1)}
          type="button"
          variant="standard"
        />
        <button
          className="saay-calendar__title saay-state"
          disabled={disabled}
          id={labelId}
          {...(onYearTitleClick === undefined ? {} : { onClick: onYearTitleClick })}
          type="button"
        >
          {toPersianDigits(String(year))}
          {onYearTitleClick === undefined ? null : <Icon name="chevron-down" />}
        </button>
        <IconButton
          disabled={disabled}
          icon="chevron-left"
          label="سال بعد"
          onClick={() => onYearChange(year + 1)}
          type="button"
          variant="standard"
        />
      </div>
      {/* Plain buttons in the labelled group: a `grid` role would promise grid keyboard navigation. */}
      <div className="saay-month-picker__grid">
        {SOLAR_HIJRI_MONTHS_FA.map((name, index) => {
          const monthNumber = index + 1;
          const bounds = solarMonthBounds(year, monthNumber);
          const disabledMonth =
            disabled === true ||
            (min !== undefined && compareLocalDate(bounds.end, min) < 0) ||
            (max !== undefined && compareLocalDate(bounds.start, max) > 0);
          const selected = month === monthNumber;
          return (
            <button
              aria-pressed={selected}
              className="saay-month-picker__cell saay-state"
              data-selected={selected || undefined}
              disabled={disabledMonth}
              key={name}
              onClick={() => onMonthChange(monthNumber)}
              type="button"
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const YEARS_PER_PAGE = 12;

export interface YearPickerProps {
  readonly disabled?: boolean;
  readonly max?: LocalDate;
  readonly min?: LocalDate;
  readonly onYearChange: (year: number) => void;
  readonly year: number;
}

/** Solar Hijri year grid, twelve years a page (owner: pick the year like the month). */
export function YearPicker({ disabled, max, min, onYearChange, year }: YearPickerProps) {
  const labelId = useId();
  const [pageStart, setPageStart] = useState(year - 5);
  const years = Array.from({ length: YEARS_PER_PAGE }, (_, index) => pageStart + index);
  const first = years[0] ?? year;
  const last = years[years.length - 1] ?? year;
  return (
    <div aria-labelledby={labelId} className="saay-month-picker" role="group">
      <div className="saay-calendar__header">
        <IconButton
          disabled={disabled}
          icon="chevron-right"
          label="سال‌های قبل"
          onClick={() => setPageStart(pageStart - YEARS_PER_PAGE)}
          type="button"
          variant="standard"
        />
        <span className="saay-calendar__title" id={labelId}>
          {toPersianDigits(`${first} – ${last}`)}
        </span>
        <IconButton
          disabled={disabled}
          icon="chevron-left"
          label="سال‌های بعد"
          onClick={() => setPageStart(pageStart + YEARS_PER_PAGE)}
          type="button"
          variant="standard"
        />
      </div>
      <div className="saay-month-picker__grid">
        {years.map((candidate) => {
          const disabledYear =
            disabled === true ||
            (min !== undefined && compareLocalDate(solarMonthBounds(candidate, 12).end, min) < 0) ||
            (max !== undefined && compareLocalDate(solarMonthBounds(candidate, 1).start, max) > 0);
          const selected = candidate === year;
          return (
            <button
              aria-pressed={selected}
              className="saay-month-picker__cell saay-state"
              data-selected={selected || undefined}
              disabled={disabledYear}
              key={candidate}
              onClick={() => onYearChange(candidate)}
              type="button"
            >
              {toPersianDigits(String(candidate))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface DateRangeValue {
  readonly end: LocalDate | '';
  readonly start: LocalDate | '';
}

export interface DateRangePickerProps {
  readonly defaultOpen?: boolean;
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly label?: string;
  readonly max?: LocalDate;
  readonly maxSpanDays?: number;
  readonly min?: LocalDate;
  readonly onChange: (value: DateRangeValue) => void;
  readonly today?: LocalDate;
  readonly value: DateRangeValue;
}

function formatRangeSummary(value: DateRangeValue): string {
  if (value.start === '') {
    return '';
  }
  const start = formatLocalDate(value.start, 'numeric');
  if (value.end === '') {
    return start;
  }
  const end = formatLocalDate(value.end, 'numeric');
  const count = rangeDayCount(value.start, value.end);
  return `${start} تا ${end} (${toPersianDigits(String(count))} روز)`;
}

/** D3: range calendar with presets and inclusive highlight. */
export function DateRangePicker({
  defaultOpen = false,
  disabled,
  error,
  label = 'بازه زمانی',
  max,
  maxSpanDays,
  min,
  onChange,
  today = todayBusinessDate(),
  value,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const rangeErrorId = `${useId()}-error`;
  const windowClass = useWindowClass('expanded');
  const presets = useMemo(() => dateRangePresets(today), [today]);
  const visible = visibleMonthForValue(value.start, min, today);
  const summary = formatRangeSummary(value);
  const rangeError =
    value.start !== '' && value.end !== '' && compareLocalDate(value.end, value.start) < 0
      ? 'تاریخ پایان باید بعد از شروع باشد.'
      : undefined;

  const calendar = (
    <div className="saay-date-range">
      <SegmentedButtons
        disabled={disabled === true}
        label="بازه‌های آماده"
        onChange={(id) => {
          const preset = presets.find((entry) => entry.id === id);
          if (preset !== undefined) {
            onChange({ end: preset.end, start: preset.start });
          }
        }}
        options={presets.map((preset) => ({
          key: preset.id,
          label: preset.label,
        }))}
        value={undefined}
      />
      <SolarCalendarPanel
        mode="range"
        onRangeChange={(next) => {
          onChange(next);
          // The desktop popover closes once the range is complete, so the form's own actions are
          // reachable at once; the phone sheet keeps its «اعمال».
          if (windowClass !== 'compact' && !defaultOpen && next.start !== '' && next.end !== '') {
            setOpen(false);
          }
        }}
        range={value}
        today={today}
        visibleMonth={visible}
        {...(disabled === undefined ? {} : { disabled })}
        {...(max === undefined ? {} : { max })}
        {...(maxSpanDays === undefined ? {} : { maxSpanDays })}
        {...(min === undefined ? {} : { min })}
      />
    </div>
  );

  const fieldTrigger = (
    <div className="saay-date-range-trigger">
      <button
        aria-describedby={
          rangeError !== undefined || error !== undefined ? rangeErrorId : undefined
        }
        className="saay-date-range-trigger__main saay-state"
        disabled={disabled}
        onClick={() => setOpen(true)}
        type="button"
      >
        {/* A Persian sentence («… تا … (۵ روز)»): RTL, or it reads back to front. */}
        <span>{summary === '' ? 'انتخاب بازه' : summary}</span>
        {value.start === '' ? <Icon name="calendar-today" /> : null}
      </button>
      {value.start !== '' ? (
        <IconButton
          disabled={disabled}
          icon="close"
          label="پاک کردن"
          onClick={() => onChange({ end: '', start: '' })}
          type="button"
          variant="standard"
        />
      ) : null}
    </div>
  );

  const trigger =
    windowClass === 'compact' || defaultOpen ? (
      fieldTrigger
    ) : (
      <PopoverPrimitive.Anchor asChild>{fieldTrigger}</PopoverPrimitive.Anchor>
    );

  return (
    <div className="saay-date-range-field">
      <span className="saay-textfield__label" onPointerEnter={revealTruncatedLabel}>
        {label}
      </span>
      {windowClass === 'compact' ? (
        <>
          {trigger}
          <BottomSheet
            footer={
              <button
                className="saay-button saay-state"
                data-appearance="filled"
                onClick={() => setOpen(false)}
                type="button"
              >
                <span className="saay-button__label">اعمال</span>
              </button>
            }
            onOpenChange={setOpen}
            open={open}
            stickyFooter
            title={label}
          >
            {calendar}
          </BottomSheet>
        </>
      ) : defaultOpen ? (
        <>
          {trigger}
          <div className="saay-popover__content saay-date-popover" data-testid="date-range-panel">
            {calendar}
          </div>
        </>
      ) : (
        <PopoverPrimitive.Root onOpenChange={setOpen} open={open}>
          {trigger}
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="end"
              className="saay-popover__content saay-date-popover"
              dir="rtl"
              onOpenAutoFocus={(event) => event.preventDefault()}
              sideOffset={6}
            >
              {calendar}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
      <FieldSupportSlot
        className="saay-textfield__support"
        error={rangeError ?? error}
        errorClassName="saay-textfield__error"
        errorId={rangeErrorId}
      />
    </div>
  );
}

interface SolarCalendarPanelProps {
  readonly disabled?: boolean;
  readonly label?: string;
  readonly max?: LocalDate;
  readonly maxSpanDays?: number;
  readonly min?: LocalDate;
  readonly mode: 'single' | 'range';
  readonly onRangeChange?: (value: DateRangeValue) => void;
  readonly onSelect?: (value: LocalDate) => void;
  readonly range?: DateRangeValue;
  readonly today: LocalDate;
  readonly value?: LocalDate | '';
  readonly visibleMonth: { readonly month: number; readonly year: number };
}

function SolarCalendarPanel({
  disabled,
  label = 'تقویم',
  max,
  maxSpanDays,
  min,
  mode,
  onRangeChange,
  onSelect,
  range,
  today,
  value = '',
  visibleMonth,
}: SolarCalendarPanelProps) {
  const [view, setView] = useState<CalendarPanelView>('day');
  const [year, setYear] = useState(visibleMonth.year);
  const [month, setMonth] = useState(visibleMonth.month);
  const [focused, setFocused] = useState<LocalDate>(value === '' ? today : value);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setYear(visibleMonth.year);
    setMonth(visibleMonth.month);
  }, [visibleMonth.month, visibleMonth.year]);

  useEffect(() => {
    if (value !== '') {
      setFocused(value);
    }
  }, [value]);

  const grid = buildSolarMonthGrid({
    month,
    year,
    ...(max === undefined ? {} : { max }),
    ...(min === undefined ? {} : { min }),
  });
  const title = `${SOLAR_HIJRI_MONTHS_FA[month - 1] ?? ''} ${toPersianDigits(String(year))}`;

  function selectDay(localDate: LocalDate) {
    if (isDayDisabled(localDate, min, max)) {
      return;
    }
    if (mode === 'single') {
      onSelect?.(localDate);
      return;
    }
    const current = range ?? { end: '', start: '' };
    const next = selectRangeClick(current.start, current.end, localDate);
    if (next.end !== '' && maxSpanDays !== undefined) {
      const clamped = clampRangeEndToMaxSpan(next.start, next.end, maxSpanDays);
      onRangeChange?.({ end: clamped, start: next.start });
      return;
    }
    onRangeChange?.(next);
  }

  function onGridKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled === true) {
      return;
    }
    const moved = moveFocusedDay(focused, event.key);
    if (moved === null) {
      return;
    }
    event.preventDefault();
    setFocused(moved);
    const solar = grid.weeks.flat().find((cell) => cell.localDate === moved)?.solar;
    if (solar !== undefined && solar.month !== month) {
      setMonth(solar.month);
      setYear(solar.year);
    }
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-date="${moved}"]`)?.focus();
  }

  if (view === 'year') {
    return (
      <YearPicker
        onYearChange={(nextYear) => {
          setYear(nextYear);
          setView('month');
        }}
        year={year}
        {...(disabled === undefined ? {} : { disabled })}
        {...(max === undefined ? {} : { max })}
        {...(min === undefined ? {} : { min })}
      />
    );
  }

  if (view === 'month') {
    return (
      <MonthPicker
        month={month}
        onMonthChange={(nextMonth) => {
          setMonth(nextMonth);
          setView('day');
        }}
        onYearChange={setYear}
        onYearTitleClick={() => setView('year')}
        year={year}
        {...(disabled === undefined ? {} : { disabled })}
        {...(max === undefined ? {} : { max })}
        {...(min === undefined ? {} : { min })}
      />
    );
  }

  return (
    <div className="saay-calendar" onKeyDown={onGridKeyDown}>
      <div className="saay-calendar__header">
        <IconButton
          disabled={disabled}
          icon="chevron-right"
          label="ماه قبل"
          onClick={() => {
            const prev = shiftSolarMonth(year, month, -1);
            setYear(prev.year);
            setMonth(prev.month);
          }}
          type="button"
          variant="standard"
        />
        <button
          className="saay-calendar__title saay-state"
          disabled={disabled}
          onClick={() => setView('month')}
          type="button"
        >
          {title}
        </button>
        <IconButton
          disabled={disabled}
          icon="chevron-left"
          label="ماه بعد"
          onClick={() => {
            const next = shiftSolarMonth(year, month, 1);
            setYear(next.year);
            setMonth(next.month);
          }}
          type="button"
          variant="standard"
        />
      </div>
      <div className="saay-calendar__chips">
        <button
          className="saay-chip saay-state"
          disabled={disabled}
          onClick={() => selectDay(today)}
          type="button"
        >
          امروز
        </button>
      </div>
      <div aria-label={label} className="saay-calendar__grid" ref={gridRef} role="grid">
        <div className="saay-calendar__weekdays" role="row">
          {SOLAR_WEEKDAY_HEADERS_FA.map((weekday) => (
            <span className="saay-calendar__weekday" key={weekday} role="columnheader">
              {weekday}
            </span>
          ))}
        </div>
        {grid.weeks.map((week, weekIndex) => (
          <div className="saay-calendar__week" key={weekIndex} role="row">
            {week.map((cell) => {
              const selected =
                mode === 'single'
                  ? value === cell.localDate
                  : range !== undefined &&
                    range.start !== '' &&
                    (range.start === cell.localDate ||
                      (range.end !== '' &&
                        isBetweenInclusive(cell.localDate, range.start, range.end)));
              const inRange =
                mode === 'range' &&
                range !== undefined &&
                range.start !== '' &&
                range.end !== '' &&
                isBetweenInclusive(cell.localDate, range.start, range.end);
              return (
                <button
                  aria-selected={selected || undefined}
                  className="saay-calendar__day saay-state"
                  data-date={cell.localDate}
                  data-friday={cell.isFriday || undefined}
                  data-in-month={cell.inMonth || undefined}
                  data-in-range={inRange || undefined}
                  data-selected={selected || undefined}
                  data-today={cell.localDate === today || undefined}
                  disabled={disabled === true || cell.disabled || !cell.inMonth}
                  key={cell.localDate}
                  onClick={() => selectDay(cell.localDate)}
                  onFocus={() => setFocused(cell.localDate)}
                  role="gridcell"
                  tabIndex={cell.localDate === focused ? 0 : -1}
                  type="button"
                >
                  {toPersianDigits(String(cell.solar.day))}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
